import {animate, style, transition, trigger} from '@angular/animations';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ToastrService} from 'ngx-toastr';

import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IPersonalEditMode} from 'src/app/interfaces/employee-edit-mode.interface';
import {Tab} from 'src/app/interfaces/employee.interface';
import {IMessengers} from 'src/app/interfaces/messengers.interface';
import {
  IDirtyPhoneNumbers,
  IEmail,
  IPersonalArrays,
  IPersonalData,
  ISocialNetwork,
} from 'src/app/interfaces/personal-data.interface';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {EmployeeService} from 'src/app/services/employee.service';
import {roles} from 'src/app/shared/utils/roles';
import {Utils} from 'src/app/shared/utils/utils';
import {emailValidationPattern} from 'src/app/validators/letterValidators';

/**
 * Component for personal data tab
 */
@UntilDestroy()
@Component({
  selector: 'app-personal-data',
  templateUrl: './personal-data.component.html',
  styleUrls: ['./personal-data.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({transform: 'translateY(-100%)', opacity: 0}),
        animate('500ms', style({transform: 'translateY(0)', opacity: 1})),
      ]),
      transition(':leave', [
        style({transform: 'translateY(0)', opacity: 1}),
        animate('500ms', style({transform: 'translateY(-100%)', opacity: 0})),
      ]),
    ]),
  ],
})
export class PersonalDataComponent implements OnInit {
  public personalDataForm!: UntypedFormGroup;
  public statusOptions = ['Active', 'Passed', 'Failed'];
  public phoneMask: string = '+#00000000000';
  phonePatterns = {
    '#': {pattern: new RegExp('[1-9]')},
    '0': {pattern: new RegExp('[0-9]')},
  };
  public isEditable = false;
  public visibleFields = false;
  public allItems: string[] = [
    'phoneNumber',
    'email',
    'linkedinProfile',
    'facebookProfile',
    'skype',
  ];
  public formValueDefault!: IPersonalData;
  public dataForArraysDefault!: IPersonalArrays;
  public changed = false;
  public relationships: IDropdownObject[] = [];
  public clothesSize: IDropdownObject[] = [];
  public countryList: IDropdownObject[] = [];
  public phoneNumbersComponentData!: IMessengers[];
  public createdPhoneNumbers: IMessengers[] = [];
  public firstNumber: IMessengers[] = [
    {
      phoneNumber: '',
      isTelegram: false,
      isWhatsApp: false,
      isViber: false,
      id: null,
    },
  ];
  public firstEmail: IEmail[] = [
    {
      email: '',
      id: null,
      employeeId: null,
    },
  ];
  public firstSocialNetwork: ISocialNetwork[] = [
    {
      socialNetworkLink: '',
      type: {
        name: '',
        id: 0,
      },
      employeeId: null,
      id: null,
    },
  ];
  public employeeId!: number;
  public firstLoad = true;
  public isAccountant!: boolean;
  public isAdmin!: boolean;
  public ableToEdit: boolean = true;
  public isEqualObjects = Utils.isEqualObjects;
  public yesNoDropdown = [
    {id: 0, name: 'No'},
    {id: 1, name: 'Yes'},
  ];
  public isFieldInvalid = Utils.isFieldInvalid;
  public validateAllFormFields = Utils.validateAllFormFields;
  @ViewChild('saveButton') saveButton!: ElementRef;
  @ViewChild('visibleContents') visibleContents!: ElementRef;

  constructor(
    private _fb: UntypedFormBuilder,
    private closeHandler: CloseHandlerService,
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private access: AccessManagementService,
    private notifier: ToastrService
  ) {}

  ngOnInit(): void {
    this.employeeId = +this.route.parent?.snapshot.params['id'];
    this.personalDataForm = this.buildForm();
    this.employeeService
      .getTabData<IPersonalData>(Tab.Personal, this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe((data: IPersonalData) => {
        this.personalDataForm.patchValue(data);
        if (data.maritalStatus) {
          this.maritalStatus.setValue(this.yesNoDropdown[1]);
        } else {
          this.maritalStatus.setValue(this.yesNoDropdown[0]);
        }
        this.createAllFormGroup(data);
        if (data.contactPerson?.relationship) {
          this.relationships = [data.contactPerson.relationship];
        }
        if (data.hoodieClothesSize) {
          this.clothesSize = [data.hoodieClothesSize];
        }
        if (data.tShirtClothesSize) {
          this.clothesSize = [data.tShirtClothesSize];
        }
        if (data.country?.name) {
          this.countryList = [data.country];
        } else {
          this.personalDataForm.get('country')?.setValue(null);
        }
        if (data.insurence) {
          this.insurence.setValue(this.yesNoDropdown[1]);
        } else {
          this.insurence.setValue(this.yesNoDropdown[0]);
        }
        this.personalDataForm.controls['residenceAddress'].setValue(
          data.addressOfActualResidence
        );
        this.formValueDefault = this.personalDataForm.value;
        this.dataForArraysDefault = {
          phoneNumbers: data.phoneNumbers,
          socialNetworks: data.socialNetworks,
          emails: data.emails,
        };
      });
    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.changed = true;
      this.personalDataForm.markAsUntouched();
      this.isEditable = false;
      this.personalDataForm.patchValue(this.formValueDefault);
      this.createAllFormGroup(this.dataForArraysDefault);
      this.changed = false;
    });
    this.personalDataForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: IPersonalData) => {
        if (
          value &&
          this.isEditable &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });
    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    if (this.isAccountant) this.ableToEdit = true;

    this.isAdmin = this.access.checkPermissions(roles.ADMIN);
    if (this.isAdmin) this.ableToEdit = false;
  }

  @HostListener('document:click', ['$event'])
  clickOut(event: MouseEvent) {
    if (this.visibleFields && this.isEditable) {
      this.visibleFields = !!this.visibleContents.nativeElement.contains(
        event.target
      );
    }
  }
  /**
   * Method that display clicked  field on form.
   * Added new field with form group according to it name
   * @param i index of the clicked element
   */
  addColumn(i: number): void {
    const nameOfField = this.allItems[i];

    if (nameOfField.includes('email')) {
      this.createEmailGroup();
    }
    if (nameOfField.includes('phoneNumber')) {
      this.addPhoneNumber();
    }
    if (nameOfField.includes('facebook')) {
      this.createNewSocialNetwork('Facebook', 1);
    }
    if (nameOfField.includes('linkedin')) {
      this.createNewSocialNetwork('Linkedin', 0);
    }
    if (nameOfField.includes('skype')) {
      this.createNewSocialNetwork('Skype', 2);
    }
  }

  /**
   * Method that switch between showing additional fields
   * @param event standart angular's event
   */
  showFields(event: Event) {
    event.preventDefault();
    this.visibleFields = !this.visibleFields;
  }

  /**
   * Method that switch between edit and read modes.
   * If it's edit mode, get dropdowns data from the server
   * @param event standart Angular's event
   */
  switchEdit(event: Event) {
    event.preventDefault();
    this.isEditable = !this.isEditable;
    this.visibleFields = false;
    if (this.isEditable) {
      if (this.firstLoad) {
        this.employeeService
          .getDropdownItems<IPersonalEditMode>(Tab.Personal)
          .pipe(untilDestroyed(this))
          .subscribe((data: IPersonalEditMode) => {
            this.relationships = data.Relationship;
            this.clothesSize = data.ClothesSize;
            this.countryList = data.Country;
            this.firstLoad = false;
          });
      }
    }
  }

  /**
   * Event on click Save button.
   * According to BE, object should has array of created or updated fields for formArrays.
   * Check wether id is present and input is dirty, push it to update array.
   * If id is null and input is dirty, push it to create array.
   * Send updated object to the server.
   */
  save() {
    if (this.personalDataForm.invalid) {
      this.validateAllFormFields(this.personalDataForm);
      return;
    }

    // Temporary remove empty fields while messengers don't have validations yet
    let array = [];
    array = this.phoneNumbersArray.controls.filter((element, index) => {
      return this.phoneNumbersControls(index).value.phoneNumber;
    });
    this.phoneNumbersArray.clear();
    this.phoneNumbersComponentData = [];
    for (const element of array) {
      this.phoneNumbersArray.push(element);
      this.phoneNumbersComponentData.push(element.value);
    }

    this.formValueDefault = this.personalDataForm.value;
    this.closeHandler.notSaved = false;
    const contactPerson = this.personalDataForm.controls[
      'contactPerson'
    ] as AbstractControl;
    let postObject: IPersonalData;
    const maritalStatus =
      this.maritalStatus.value.name === 'Yes' ? true : false;
    const insurence = this.insurence.value.name === 'Yes' ? true : false;
    if (contactPerson.value.phoneNumber === '')
      contactPerson.value.phoneNumber = null;
    /**
     * Final object, that will be send to the server
     */
    postObject = {
      ...this.personalDataForm.value,
      insurence,
      maritalStatus,
    };
    this.employeeService
      .postTabData(Tab.Personal, postObject)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isEditable = !this.isEditable;
        this.notifier.success('Saved');
        this.visibleFields = false;
      });
    this.dataForArraysDefault = {
      phoneNumbers: this.personalDataForm.controls['phoneNumbers'].value,
      socialNetworks: this.personalDataForm.controls['socialNetworks'].value,
      emails: this.personalDataForm.controls['emails'].value,
    };
  }

  /**
   * Get form array of children
   */
  get children(): UntypedFormControl {
    return this.personalDataForm.get('childrenCount') as UntypedFormControl;
  }

  /**
   * Get form array of numbers
   */
  get phoneNumbersArray(): UntypedFormArray {
    return this.personalDataForm.get('phoneNumbers') as UntypedFormArray;
  }

  /**
   * Get form array of emails
   */
  get emailsArray(): UntypedFormArray {
    return this.personalDataForm.get('emails') as UntypedFormArray;
  }

  /**
   * Get form array of social networks
   */
  get socialNetworksArray(): UntypedFormArray {
    return this.personalDataForm.get('socialNetworks') as UntypedFormArray;
  }

  get contactPerson(): UntypedFormGroup {
    return this.personalDataForm.get('contactPerson') as UntypedFormGroup;
  }

  /**
   * Add new child by clicking on the button
   */

  get maritalStatus(): AbstractControl {
    return this.personalDataForm.get('maritalStatus')!;
  }

  get insurence(): AbstractControl {
    return this.personalDataForm.get('insurence')!;
  }

  emailsArrayControls(index: number): UntypedFormGroup {
    return this.emailsArray.controls[index] as UntypedFormGroup;
  }

  socialNetworksControls(index: number): UntypedFormGroup {
    return this.socialNetworksArray.controls[index] as UntypedFormGroup;
  }

  phoneNumbersControls(index: number): UntypedFormGroup {
    return this.phoneNumbersArray.controls[index] as UntypedFormGroup;
  }
  /**
   * Method that update the value of define elemnt of phoneNumbersArray
   * @param event data from messenger compontns
   * @param index index of the array
   */
  setPhoneNumber(event: IDirtyPhoneNumbers, index: number) {
    this.phoneNumbersArray.controls[index].patchValue(event.value);
    if (event.dirty) {
      this.phoneNumbersArray.controls[index].markAsDirty();
    }
  }

  /**
   * Method that create new form group for email.
   * If the array is empty, set default form group for the first email
   */
  createEmailGroup() {
    const newEmail = this._fb.group({
      ...this.firstEmail[0],
    });
    newEmail.controls['email'].addValidators([
      Validators.required,
      Validators.pattern(emailValidationPattern),
      Validators.maxLength(30),
    ]);
    newEmail.controls['email'].updateValueAndValidity();
    if (this.emailsArray.length === 0) {
      this.createFormGroup(this.firstEmail, 'emails', 'email');
    } else {
      this.emailsArray.push(newEmail);
    }
  }

  /**
   * Add new phone number to the tab.
   * If the array is empty set default form group and push it to the array
   */
  addPhoneNumber() {
    const newNum = this._fb.group({
      ...this.firstNumber[0],
    });
    if (this.phoneNumbersArray.length === 0) {
      this.createFormGroup(this.firstNumber, 'phoneNumbers', 'phoneNumber');
    } else {
      this.phoneNumbersArray.push(newNum);
    }
    this.phoneNumbersComponentData.push(this.firstNumber[0]);
  }

  /**
   * Method that accept data from the server and created form groups according to data.
   * @param data array of data from the server
   * @param control control name, where we will set the control
   */
  createFormGroup(
    data: IMessengers[] | ISocialNetwork[] | IEmail[],
    control: string,
    requiredField: string
  ) {
    const newGroupArray = data.map(
      (fields: IMessengers | ISocialNetwork | IEmail) => {
        const formGroup = this._fb.group({
          ...fields,
        });
        // Temporary don't add the validation for the phone numbers
        if (requiredField !== 'phoneNumber')
          formGroup.controls[requiredField].addValidators([
            Validators.required,
            Validators.maxLength(40),
          ]);
        if (requiredField === 'email')
          formGroup.controls[requiredField].addValidators(
            Validators.pattern(emailValidationPattern)
          );
        return formGroup;
      }
    );
    const groupArray: UntypedFormArray = this._fb.array(newGroupArray);
    this.personalDataForm.setControl(control, groupArray);
  }

  /**
   * Method that create new group according to the clicked social network.
   * If the array is empty, throught the switch case set default value
   * @param linkName name of social network
   * @param linkId id of the social network
   * @param emplId current employee id
   */
  createNewSocialNetwork(linkName: string, linkId: number, emplId?: number) {
    const newSocialNetwork: ISocialNetwork = {
      socialNetworkLink: '',
      type: {
        name: linkName,
        id: linkId,
      },
      employeeId: emplId,
      id: null,
    };
    const newSocial = this._fb.group({
      ...newSocialNetwork,
    });
    newSocial.controls['socialNetworkLink'].addValidators([
      Validators.required,
      Validators.maxLength(40),
    ]);
    newSocial.controls['socialNetworkLink'].updateValueAndValidity();
    if (this.socialNetworksArray.length === 0) {
      switch (linkName) {
        case 'Facebook':
          this.firstSocialNetwork[0].type.id = linkId;
          this.firstSocialNetwork[0].type.name = linkName;
          break;
        case 'Skype':
          this.firstSocialNetwork[0].type.id = linkId;
          this.firstSocialNetwork[0].type.name = linkName;
          break;
        case 'Linkedin':
          this.firstSocialNetwork[0].type.id = linkId;
          this.firstSocialNetwork[0].type.name = linkName;
          break;
      }
      this.createFormGroup(
        this.firstSocialNetwork,
        'socialNetworks',
        'socialNetworkLink'
      );
    } else {
      this.socialNetworksArray.push(newSocial);
    }
  }

  /**
   * The method creates all form groups for the personal tab
   * @param data data from the server for the personal tab
   */
  createAllFormGroup(data: IPersonalArrays): void {
    this.createFormGroup(data.emails, 'emails', 'email');
    this.createFormGroup(data.phoneNumbers, 'phoneNumbers', 'phoneNumber');
    this.createFormGroup(
      data.socialNetworks,
      'socialNetworks',
      'socialNetworkLink'
    );
    this.phoneNumbersComponentData = data.phoneNumbers;
  }

  removeItem(index: number, selectedArray: UntypedFormArray) {
    selectedArray.removeAt(index);
  }

  private buildForm(): FormGroup {
    return this._fb.group({
      id: new UntypedFormControl(null),
      country: new UntypedFormControl(null, [Validators.required]),
      city: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(20),
      ]),
      residenceAddress: new UntypedFormControl(null, [
        Validators.maxLength(100),
      ]),
      postOfficeAddress: new UntypedFormControl(null, [
        Validators.maxLength(100),
      ]),
      insurence: new UntypedFormControl(null),
      tShirtClothesSize: new UntypedFormControl(null),
      hoodieClothesSize: new UntypedFormControl(null),
      birthday: new UntypedFormControl(null),
      maritalStatus: new UntypedFormControl(null),
      contactPerson: new UntypedFormGroup({
        name: new UntypedFormControl('', [Validators.maxLength(100)]),
        phoneNumber: new UntypedFormControl(null),
        relationship: new UntypedFormControl(null),
        id: new UntypedFormControl(null),
      }),
      childrenCount: new UntypedFormControl(0, [
        Validators.min(0),
        Validators.required,
        Validators.pattern(/^(0|[1-9][0-9]*)$/),
      ]),
      phoneNumbers: new UntypedFormArray([]),
      emails: new UntypedFormArray([]),
      socialNetworks: new UntypedFormArray([]),
    });
  }
}
